/*
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import appTheme from '../../theme/appTheme.json';
import FeedbackConfirmationDialog from './FeedbackConfirmationDialog';
import { ajaxRaw } from '../../../../utils/StoreUtils';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackDialog = ({ open, onClose }: FeedbackDialogProps) => {
  const [feedback, setFeedback] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const showError = (submitted && feedback.trim() === '') || !!submitError;

  const handleSubmit = () => {
    setSubmitted(true);
    setSubmitError('');
    if (feedback.trim() === '') return;

    setIsSubmitting(true);
    ajaxRaw.postToBasePath('/feedback/submit', { feedback, isUrgent: urgent }).subscribe({
      next: () => {
        setIsSubmitting(false);
        onClose();
        setShowConfirmation(true);
      },
      error: () => {
        setIsSubmitting(false);
        setSubmitError('Failed to submit feedback. Please try again.');
      },
    });
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setFeedback('');
    setUrgent(false);
    setSubmitted(false);
    setSubmitError('');
    onClose();
  };

  const handleClose = () => {
    setFeedback('');
    setUrgent(false);
    setSubmitted(false);
    setSubmitError('');
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            background: appTheme.primaryBackgroundColor,
            borderRadius: '12px',
            width: 640,
            maxWidth: 640,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: 3,
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: appTheme.viasatFontFamilyHeavy,
              fontWeight: 800,
              fontSize: '20px',
              lineHeight: '28px',
              letterSpacing: '0.2px',
              color: appTheme.primaryTextColor,
            }}
          >
            We value your feedback.
          </Typography>
          <IconButton onClick={handleClose} aria-label="close" sx={{ color: appTheme.primaryTextColor, p: 0, mt: '2px' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, pb: 3, pt: 0 }}>
          <Box>
            <Typography
              sx={{
                fontFamily: appTheme.appFontFamily,
                fontSize: appTheme.defaultFontSize,
                lineHeight: '24px',
                color: appTheme.primaryTextColor,
                mb: 2,
              }}
            >
              Please provide your feedback using the form below.
            </Typography>
            <Typography
              sx={{
                fontFamily: appTheme.appFontFamily,
                fontSize: appTheme.defaultFontSize,
                lineHeight: '24px',
                color: appTheme.primaryTextColor,
              }}
            >
              If necessary or requested, we will respond to the email associated with your account.
            </Typography>
          </Box>
          <TextField
            multiline
            minRows={8}
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Enter your feedback here..."
            fullWidth
            error={showError}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: appTheme.mptPrimaryBackgroundColor,
                borderRadius: '4px',
                fontFamily: appTheme.appFontFamily,
                fontSize: appTheme.defaultFontSize,
                lineHeight: '24px',
                color: appTheme.primaryTextColor,
                '& fieldset': {
                  borderColor: showError ? appTheme.errorTextColor : appTheme.secondaryHeaderTextColor,
                },
                '&:hover fieldset': {
                  borderColor: showError ? appTheme.errorTextColor : appTheme.secondaryHeaderTextColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: showError ? appTheme.errorTextColor : appTheme.mptLightBlue,
                },
              },
              '& .MuiOutlinedInput-input': {
                '&::placeholder': {
                  color: appTheme.secondaryHeaderTextColor,
                  opacity: 1,
                },
              },
            }}
          />
          {showError && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px', width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pt: '2px', flexShrink: 0 }}>
                <ErrorOutlineIcon sx={{ fontSize: 16, color: appTheme.errorTextColor }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: appTheme.appFontFamily,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: appTheme.errorTextColor,
                }}
              >
                {submitError || 'Please enter a message.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 3,
            borderTop: `1px solid ${appTheme.mptRowBorderBottom}`,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden', pr: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={urgent}
                  onChange={e => setUrgent(e.target.checked)}
                  sx={{
                    color: appTheme.grayTextColor,
                    p: 0,
                    mr: 1,
                    borderRadius: '2px',
                    '&.Mui-checked': {
                      color: appTheme.mptLightBlue,
                    },
                  }}
                />
              }
              label="Mark as urgent."
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': {
                  fontFamily: appTheme.appFontFamily,
                  fontWeight: 400,
                  fontSize: appTheme.defaultFontSize,
                  lineHeight: '24px',
                  color: appTheme.primaryTextColor,
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                height: 40,
                px: 3,
                borderColor: appTheme.primaryTextColor,
                color: appTheme.primaryTextColor,
                fontFamily: appTheme.appFontFamily,
                fontWeight: 600,
                fontSize: appTheme.defaultFontSize,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: appTheme.primaryTextColor,
                  background: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                height: 40,
                px: 3,
                background: appTheme.mptLightBlue,
                color: appTheme.darkGrayTextColor,
                fontFamily: appTheme.appFontFamily,
                fontWeight: 600,
                fontSize: appTheme.defaultFontSize,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  background: appTheme.mptLightBlue,
                  boxShadow: 'none',
                  opacity: 0.9,
                },
              }}
            >
              {isSubmitting ? <CircularProgress size={20} sx={{ color: appTheme.darkGrayTextColor }} /> : 'Submit'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <FeedbackConfirmationDialog open={showConfirmation} onClose={handleConfirmationClose} />
    </>
  );
};

export default FeedbackDialog;
